import React, { useEffect, useState, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { makeStyles } from '@material-ui/core/styles'

const useStyles = makeStyles(() => ({
  // Tas de neige sur les cards
  snowPile: {
    position: 'absolute',
    top: 0,
    left: '50%',
    transform: 'translateX(-50%) translateY(-85%)',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.97) 0%, rgba(248,252,255,0.92) 100%)',
    borderRadius: '50% 50% 0 0 / 100% 100% 0 0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    pointerEvents: 'none',
    zIndex: 10,
    // Petite bosse à gauche
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '-3px',
      left: '10%',
      width: '28%',
      height: '75%',
      background: 'rgba(255,255,255,0.97)',
      borderRadius: '50%',
    },
    // Petite bosse à droite
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '-2px',
      right: '15%',
      width: '22%',
      height: '60%',
      background: 'rgba(255,255,255,0.95)',
      borderRadius: '50%',
    },
  },
  
  // Neige au sol
  groundSnow: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '20px',
    background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.65) 40%, rgba(255,255,255,0.9) 100%)',
    pointerEvents: 'none',
    zIndex: 1001,
  },
  
  // Tas de neige au sol
  snowDrift: {
    position: 'fixed',
    bottom: 0,
    background: 'linear-gradient(180deg, rgba(255,255,255,0.97) 0%, rgba(255,255,255,0.93) 100%)',
    borderRadius: '50% 50% 0 0 / 100% 100% 0 0',
    pointerEvents: 'none',
    zIndex: 1002,
  },
}))

// Composant pour un tas de neige individuel injecté dans une card
const SnowPile = ({ element, classes }) => {
  const [dimensions, setDimensions] = useState(null)
  
  useEffect(() => {
    // Force position relative sur l'élément parent si nécessaire
    const computed = window.getComputedStyle(element)
    if (computed.position === 'static') {
      element.style.position = 'relative'
    }
    element.style.overflow = 'visible'
    
    // Dimensions aléatoires mais proportionnelles à la card
    const rect = element.getBoundingClientRect()
    setDimensions({
      width: rect.width * (0.55 + Math.random() * 0.25),
      height: 5 + Math.random() * 5,
    })
  }, [element])
  
  if (!dimensions) return null
  
  return createPortal(
    <div 
      className={classes.snowPile}
      style={{
        width: dimensions.width,
        height: dimensions.height,
      }}
    />,
    element
  )
}

const SnowAccumulation = ({ enabled = true }) => {
  const classes = useStyles()
  const [cards, setCards] = useState([])
  const processedRef = useRef(new WeakSet())

  // Trouve les cards éligibles
  const findCards = useCallback(() => {
    if (!enabled) return []
    
    const selectors = [
      '.MuiCard-root',
      '.MuiPaper-root:not(.MuiDialog-paper):not(.MuiPopover-paper):not(.MuiMenu-paper):not(.MuiDrawer-paper):not(.MuiAppBar-root)',
    ]
    
    const found = []
    
    selectors.forEach(selector => {
      try {
        document.querySelectorAll(selector).forEach(el => {
          // Déjà traité ?
          if (processedRef.current.has(el)) {
            found.push(el)
            return
          }
          
          const rect = el.getBoundingClientRect()
          // Filtre les éléments trop petits
          if (rect.width < 100 || rect.height < 50) return
          
          processedRef.current.add(el)
          found.push(el)
        })
      } catch (e) {}
    })
    
    return found
  }, [enabled])

  useEffect(() => {
    if (!enabled) {
      setCards([])
      return
    }

    // Scan initial
    const timer = setTimeout(() => {
      setCards(findCards())
    }, 500)

    // Re-scan périodique pour les nouvelles cards
    const interval = setInterval(() => {
      setCards(findCards())
    }, 3000)

    return () => {
      clearTimeout(timer)
      clearInterval(interval)
    }
  }, [enabled, findCards])

  if (!enabled) return null

  return (
    <>
      {/* Tas de neige sur chaque card */}
      {cards.map((el, i) => (
        <SnowPile key={i} element={el} classes={classes} />
      ))}
      
      {/* Neige au sol */}
      <div className={classes.groundSnow} />
      <div className={classes.snowDrift} style={{ left: '2%', width: '75px', height: '11px' }} />
      <div className={classes.snowDrift} style={{ left: '15%', width: '110px', height: '14px' }} />
      <div className={classes.snowDrift} style={{ left: '35%', width: '90px', height: '12px' }} />
      <div className={classes.snowDrift} style={{ left: '55%', width: '100px', height: '13px' }} />
      <div className={classes.snowDrift} style={{ left: '75%', width: '85px', height: '11px' }} />
      <div className={classes.snowDrift} style={{ left: '90%', width: '70px', height: '10px' }} />
    </>
  )
}

export default SnowAccumulation
